/**
 * /takeover/:takeoverId — the S-23 Enemy Takeover suite (V4), party-aware:
 *
 *   target   + offer_sent → CONSENT full-screen: flavor header, then the
 *              dead-serious plain-language terms box (©takeover.consent_terms
 *              — what they can edit, what they never can, the badge, 24 h
 *              auto-expiry, the one-tap kill switch). Accept / Decline equal
 *              weight. Tone rule 4: consent boxes never joke.
 *   initiator + offer_sent → pending state: countdown + Cancel offer
 *              (instant token refund — SM-6 CANCEL).
 *   initiator + active     → sandboxed overlay EDITOR (bio + prompts ONLY —
 *              the request literally has no other fields), moderation status
 *              per edit, 2-strike warning (EC-20), countdown.
 *   target   + active      → OWNER view: hijacked preview, change-log status,
 *              countdown, and the big END THIS NOW button — one tap, zero
 *              confirmation, by design (D-003).
 *   terminal states        → honest wrap-up copy (©takeover.*).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { copy, formatCopy, resolveCopy } from "@humble/shared";
import {
  Button,
  Card,
  glitchVariants,
  HijackedBadge,
  Mascot,
  Skeleton,
  toast,
} from "../../design/index.js";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { sessionStore, useStore } from "../../lib/store.js";
import { ws } from "../../lib/ws.js";
import { useKillstreakAnnouncer } from "../killstreak/announcer-bridge.js";
import "./takeover.css";

/** v3 — the arcade header: Anton under a static chromatic fringe, tearing in
 *  with the bounded one-shot glitch (collapses under reduced motion). The
 *  fringe is aria-hidden ghost spans so the heading's accessible name stays
 *  exactly the visible line. */
function ArcadeTitle({ children }: { children: string }) {
  return (
    <motion.h1
      className="type-display-3 takeover-title"
      variants={glitchVariants}
      initial="initial"
      animate="enter"
    >
      <span className="takeover-title__ghost takeover-title__ghost--cyan" aria-hidden="true">
        {children}
      </span>
      <span className="takeover-title__ghost takeover-title__ghost--red" aria-hidden="true">
        {children}
      </span>
      {children}
    </motion.h1>
  );
}

interface TakeoverView {
  id: string;
  state: string;
  initiator: { id: string; display_name: string | null };
  target: { id: string; display_name: string | null };
  offer_expires_at: string | null;
  overlay_expires_at: string | null;
  rejected_edit_count: number;
  overlay: {
    bio: string | null;
    prompts: Array<{ prompt_id?: number; answer?: string }>;
    revision: number;
    mod_status: string;
  } | null;
}

function useCountdown(iso: string | null): string {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, []);
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "0:00";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}:${String(m).padStart(2, "0")}`;
}

const TERMINAL_COPY: Record<string, string> = {
  declined: copy.takeover.declined,
  canceled: copy.takeover.offer_canceled,
  offer_expired: "The offer expired unanswered. Token refunded.",
  expired: copy.takeover.expired,
  killed_by_owner: "The owner pulled the plug. Overlay gone.",
  killed_by_mod: "Moderation ended this takeover.",
  closed_account_event: copy.takeover.target_gone,
};

export function TakeoverScreen() {
  useKillstreakAnnouncer();
  const { takeoverId = "" } = useParams();
  const me = useStore(sessionStore, (s) => s.user?.id ?? null);
  const [view, setView] = useState<TakeoverView | null | "missing">(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!takeoverId) return;
    api
      .get("/takeovers/{takeoverId}", { params: { takeoverId } })
      .then((v) => setView(v as unknown as TakeoverView))
      .catch(() => setView("missing"));
  }, [takeoverId]);

  useEffect(() => {
    load();
    const offs = [
      ws.on("takeover.accepted", load),
      ws.on("takeover.declined", load),
      ws.on("takeover.canceled", load),
      ws.on("takeover.expired", load),
      ws.on("takeover.updated", load),
      ws.on("takeover.killed", load),
    ];
    return () => offs.forEach((off) => off());
  }, [load]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, okMsg?: string) => {
      setBusy(true);
      try {
        await fn();
        if (okMsg) toast(okMsg, "info");
        load();
      } catch (err) {
        toast(isApiError(err) ? err.message : "That did not go through.", "error");
        load();
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  if (view === null) {
    return (
      <div className="takeover-screen">
        <Skeleton style={{ height: 240 }} />
      </div>
    );
  }
  if (view === "missing") {
    return (
      <div className="takeover-screen">
        <Card padding="lg" className="takeover-scene">
          <Mascot mood="ko" />
          <p className="text-dim" style={{ margin: 0 }}>
            No such takeover — or it is not yours to see.
          </p>
        </Card>
      </div>
    );
  }

  const iAmTarget = me === view.target.id;
  const iAmInitiator = me === view.initiator.id;
  const initiatorName = view.initiator.display_name ?? "Someone";

  if (view.state === "offer_sent" && iAmTarget) {
    return (
      <ConsentScreen
        view={view}
        busy={busy}
        onAccept={() =>
          act(
            () =>
              api.post("/takeovers/{takeoverId}/accept", {
                params: { takeoverId: view.id },
                body: withEventId({}),
              }),
            "Takeover accepted. Your kill switch is live.",
          )
        }
        onDecline={() =>
          act(
            () => api.post("/takeovers/{takeoverId}/decline", { params: { takeoverId: view.id } }),
            "Declined. The canvas stays yours.",
          )
        }
      />
    );
  }

  if (view.state === "offer_sent" && iAmInitiator) {
    return (
      <PendingScreen
        view={view}
        busy={busy}
        onCancel={() =>
          act(
            () => api.post("/takeovers/{takeoverId}/cancel", { params: { takeoverId: view.id } }),
            "Offer withdrawn. Token refunded.",
          )
        }
      />
    );
  }

  if (view.state === "active" && iAmInitiator) {
    return <EditorScreen view={view} busy={busy} act={act} />;
  }

  if (view.state === "active" && iAmTarget) {
    return (
      <OwnerScreen
        view={view}
        busy={busy}
        onKill={() =>
          act(
            () => api.post("/takeovers/{takeoverId}/kill", { params: { takeoverId: view.id } }),
            "Overlay gone. Instantly.",
          )
        }
      />
    );
  }

  // Terminal states (either party) — the wrap-up, mascot flatlined.
  return (
    <div className="takeover-screen">
      <ArcadeTitle>Enemy Takeover</ArcadeTitle>
      <Card padding="lg" className="takeover-scene">
        <Mascot mood="ko" />
        <p style={{ margin: 0 }}>
          {formatCopy(TERMINAL_COPY[view.state] ?? "This takeover has ended.", {
            name: initiatorName,
            duration: "24h",
          })}
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Consent (target, S-23) — hybrid tone: light frame, dead-serious terms box
// ---------------------------------------------------------------------------------------

function ConsentScreen({
  view,
  busy,
  onAccept,
  onDecline,
}: {
  view: TakeoverView;
  busy: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const name = view.initiator.display_name ?? "Someone";
  const left = useCountdown(view.offer_expires_at);
  return (
    <div className="takeover-screen takeover-consent">
      <ArcadeTitle>Enemy Takeover offer</ArcadeTitle>
      <p>{formatCopy(copy.takeover.offer_received, { name })}</p>

      <OverlayPreview view={view} title="Exactly what would go live (pending moderation):" />

      {/* the terms box — dead serious, visually apart from the arcade frame
          (tone rule 4: consent surfaces never joke, never glow) */}
      <Card padding="lg" className="takeover-terms">
        <h2 className="takeover-terms__title">The terms, in plain language</h2>
        <p className="takeover-terms__body">{formatCopy(copy.takeover.consent_terms, { name })}</p>
        {left && (
          <p className="text-faint">
            Offer expires in <span className="type-numeric">{left}</span>. Ignoring it is also an answer.
          </p>
        )}
      </Card>

      <div className="takeover-consent__actions">
        <Button variant="default" disabled={busy} onClick={onDecline}>
          {copy.takeover.decline_label}
        </Button>
        <Button variant="default" disabled={busy} onClick={onAccept}>
          {copy.takeover.accept_label}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Pending offer (initiator)
// ---------------------------------------------------------------------------------------

function PendingScreen({
  view,
  busy,
  onCancel,
}: {
  view: TakeoverView;
  busy: boolean;
  onCancel: () => void;
}) {
  const left = useCountdown(view.offer_expires_at);
  return (
    <div className="takeover-screen">
      <ArcadeTitle>Offer pending</ArcadeTitle>
      <p className="text-dim">{copy.takeover.offer_sent}</p>
      <Card padding="lg" className="takeover-pending crt">
        <p style={{ marginTop: 0 }}>
          Waiting on {view.target.display_name ?? "the owner"}.{" "}
          {left && (
            <>
              Time left: <span className="takeover-clock type-numeric">{left}</span>.
            </>
          )}
        </p>
        <Button variant="default" disabled={busy} onClick={onCancel}>
          {copy.takeover.cancel_offer_label}
        </Button>
      </Card>
      <OverlayPreview view={view} title="Your pitch (revision 1 — immutable until accepted):" />
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Editor (initiator, ACTIVE) — bio + prompts ONLY; every edit moderated
// ---------------------------------------------------------------------------------------

function EditorScreen({
  view,
  busy,
  act,
}: {
  view: TakeoverView;
  busy: boolean;
  act: (fn: () => Promise<unknown>, okMsg?: string) => Promise<void>;
}) {
  const [bio, setBio] = useState(view.overlay?.bio ?? "");
  const [answers, setAnswers] = useState<Array<{ prompt_id: number; answer: string }>>(
    () =>
      (view.overlay?.prompts ?? [])
        .filter((p) => typeof p.prompt_id === "number")
        .map((p) => ({ prompt_id: p.prompt_id!, answer: p.answer ?? "" })),
  );
  const left = useCountdown(view.overlay_expires_at);
  const strikes = view.rejected_edit_count;

  const submit = () =>
    act(
      () =>
        api.put("/takeovers/{takeoverId}/overlay", {
          params: { takeoverId: view.id },
          body: {
            ...(bio.trim() ? { bio: bio.trim() } : {}),
            ...(answers.length > 0
              ? { prompts: answers.filter((a) => a.answer.trim().length > 0) }
              : {}),
          },
        }),
      "Edit queued for moderation. It publishes on approval.",
    );

  return (
    <div className="takeover-screen">
      <ArcadeTitle>The canvas is yours</ArcadeTitle>
      <p className="text-dim">
        Bio and prompt answers only — everything else on their profile is out of reach, by design.
        Every edit passes moderation before anyone sees it.
        {left && (
          <>
            {" "}
            Overlay ends in <span className="takeover-clock type-numeric">{left}</span>.
          </>
        )}
      </p>
      {strikes === 1 && (
        <Card padding="md" className="takeover-strike">
          {copy.takeover.edit_rejected}
        </Card>
      )}
      <Card padding="lg">
        <label className="takeover-field">
          <span className="takeover-field__label">Overlay bio (≤500)</span>
          <textarea
            className="takeover-field__input"
            maxLength={500}
            rows={5}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
        </label>
        {answers.map((a, i) => (
          <label key={a.prompt_id} className="takeover-field">
            <span className="takeover-field__label">Prompt #{a.prompt_id} answer (≤300)</span>
            <textarea
              className="takeover-field__input"
              maxLength={300}
              rows={2}
              value={a.answer}
              onChange={(e) =>
                setAnswers((prev) => prev.map((p, j) => (j === i ? { ...p, answer: e.target.value } : p)))
              }
            />
          </label>
        ))}
        <div className="takeover-editor__meta text-faint">
          Latest revision: #{view.overlay?.revision ?? 1} — {modStatusLabel(view.overlay?.mod_status)}
        </div>
        <Button variant="default" disabled={busy || (!bio.trim() && answers.length === 0)} onClick={submit}>
          Submit edit for review
        </Button>
      </Card>
      <OverlayPreview view={view} title="As visitors will see it (once approved):" />
    </div>
  );
}

function modStatusLabel(s: string | undefined): string {
  if (s === "live") return "approved and public";
  if (s === "removed" || s === "quarantined") return "rejected — previous revision stands";
  return copy.takeover.pending_review;
}

// ---------------------------------------------------------------------------------------
// Owner view (target, ACTIVE) — change-log + the kill switch
// ---------------------------------------------------------------------------------------

function OwnerScreen({
  view,
  busy,
  onKill,
}: {
  view: TakeoverView;
  busy: boolean;
  onKill: () => void;
}) {
  const left = useCountdown(view.overlay_expires_at);
  const name = view.initiator.display_name ?? "Someone";
  return (
    <div className="takeover-screen">
      <ArcadeTitle>Your profile, hijacked</ArcadeTitle>
      <p className="text-dim">
        {name} is running an overlay on your bio and prompts. Nothing else of yours can be touched.
        {left && (
          <>
            {" "}
            It self-destructs in <span className="takeover-clock type-numeric">{left}</span>.
          </>
        )}
      </p>

      {/* The kill switch: one tap, always visible, zero confirmation (D-003). */}
      <button className="takeover-kill" disabled={busy} onClick={onKill}>
        {copy.takeover.kill_button_label}
      </button>
      <p className="text-faint takeover-kill__note">
        One tap. No confirmation. Instant. That is the deal you accepted — and the one they did too.
      </p>

      <OverlayPreview view={view} title={copy.takeover.change_log_title} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Shared overlay preview — the labeled, sandboxed view (HIJACKED BY badge)
// ---------------------------------------------------------------------------------------

function OverlayPreview({ view, title }: { view: TakeoverView; title: string }) {
  const name = view.initiator.display_name ?? "?";
  const pending = view.overlay?.mod_status !== "live";
  const prompts = useMemo(
    () => (view.overlay?.prompts ?? []).filter((p) => (p.answer ?? "").length > 0),
    [view.overlay],
  );
  return (
    <Card padding="lg" className="takeover-preview crt">
      <div className="takeover-preview__head">
        <span className="text-faint">{title}</span>
        <HijackedBadge name={name} />
      </div>
      {view.overlay?.bio && <p className="takeover-preview__bio">{view.overlay.bio}</p>}
      {prompts.map((p, i) => (
        <p key={i} className="takeover-preview__prompt">
          <span className="text-faint">Prompt #{p.prompt_id}: </span>
          {p.answer}
        </p>
      ))}
      {!view.overlay?.bio && prompts.length === 0 && (
        <p className="text-faint" style={{ margin: 0 }}>
          Empty overlay.
        </p>
      )}
      <div className="text-faint takeover-preview__status">
        Revision #{view.overlay?.revision ?? 1} —{" "}
        {pending ? (resolveCopy("takeover.pending_review") ?? "pending review") : "approved"}
      </div>
    </Card>
  );
}
